import { Input, Select } from 'antd';
import { type TagProps } from 'antd5';
import i18n from 'i18next';
import { get, isString } from 'lodash';
import moment, { type Moment } from 'moment';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { TaskForOrganizationEditor } from 'components/editorModals/TaskForOrganizationEditor';
import { Tag, Tooltip } from 'components/ui';
import { UTCDatePicker } from 'components/UTCDatePicker';
import {
  type AtlasGqlAlert,
  type AtlasGqlInspectionDelivery,
  type AtlasGqlInspectionTask,
  type AtlasGqlOrganization,
  type AtlasGqlTaskStatus,
  AtlasGqlInspectionDeliveryStatus,
  AtlasGqlWorkContainerTypeOption,
} from 'types/atlas-graphql';
import { StatusTag } from 'utils/color/helpers';
import { TASK_STATUSES } from 'utils/constants';
import { inlineEditable } from 'utils/editable/InlineEditable/InlineEditable';
import { formatDate, startCase } from 'utils/format';
import { isOverdue } from 'utils/helpers';
import naturalSort from 'utils/naturalSort';

import {
  defaultNone,
  getFilterDropdown,
  getOnFilter,
  None,
  sortBy,
  truncateTooltipper,
} from '../../helpers';

const { TextArea } = Input;

export const createdFrom = {
  key: 'createdFrom',
  title: i18n.t('table.createdFrom', 'Created From'),
  dataIndex: ['alert', 'id'],
  filterProperty: 'alert.id',
  sorter: sortBy('alert.id'),
  get: (record: AtlasGqlAlert) => get(record, ['alert', 'id']),
  render: (id: string) =>
    id ? (
      <Link to={`/alerts/${id}`}>
        {i18n.t('tasks.alertWithId', 'Alert {{id}}', { id: id.split('-')[0].toUpperCase() })}
      </Link>
    ) : (
      <None />
    ),
};

const createdDateFilterConfig = {
  key: 'created',
  type: 'date',
  filterAttr: '',
  exactMatch: '',
  values: '',
  includeNoneOption: false,
};

export const created = {
  key: 'created',
  title: i18n.t('table.created', 'Created'),
  dataIndex: 'created',
  filterProperty: 'created',
  filterDropdown: getFilterDropdown(createdDateFilterConfig),
  sorter: sortBy('created'),
  get: (record: any) => get(record, 'created'),
  render: (created: string) => (created ? formatDate(created) : <None />),
};

export const createdBy = {
  key: 'createdBy',
  title: i18n.t('table.createdBy', 'Created By'),
  dataIndex: ['createdBy', 'email'],
  filterProperty: 'createdBy.email',
  sorter: sortBy('createdBy.email'),
  get: (record: any) => get(record, ['createdBy', 'email']),
  render: (email: string) => email || <None />,
};

export const createdAndCreatedBy = {
  key: 'created',
  title: i18n.t('table.created', 'Created'),
  sorter: sortBy('created'),
  filterProperty: 'created',
  filterDropdown: getFilterDropdown(createdDateFilterConfig),
  onFilter: getOnFilter(createdDateFilterConfig),
  onHeaderCell: () => ({
    'data-testid': 'tasks-created-and-by-header',
  }),
  get: (record: any) => get(record, 'created'),
  render: (record: any) => {
    const { created, createdBy } = record || {};
    if (!created) {
      return <None />;
    }

    const formattedDate = formatDate(created);
    const { email } = createdBy || {};

    if (email) {
      const title = (
        <Fragment>
          <b>{i18n.t('tasks.byLabel', 'By:')}</b> {email}
        </Fragment>
      );
      return <Tooltip title={title}>{formattedDate}</Tooltip>;
    } else {
      return formattedDate;
    }
  },
};

export const shortDescription = {
  key: 'description',
  title: i18n.t('table.description', 'Description'),
  dataIndex: 'description',
  sorter: sortBy('description'),
  filterProperty: 'description',
  get: (record: any) => get(record, 'description'),
  render: (des: string) => truncateTooltipper(des, 55),
  edit(record: any, { onChange, ...props }: { onChange: (data: any) => void }) {
    const { description } = record || {};

    return (
      <TextArea
        autoSize
        defaultValue={description}
        onChange={onChange && (e => onChange({ description: e.target.value }))}
        {...props}
      />
    );
  },
};

export const description = {
  ...shortDescription,
  render: (description: string) =>
    description ? (
      <TextArea
        value={description}
        style={{
          cursor: 'inherit',
          backgroundColor: 'inherit',
          color: 'inherit',
          resize: 'none',
          border: 0,
        }}
        disabled
        autoSize
      />
    ) : (
      <None />
    ),
};

export const name = {
  key: 'name',
  title: i18n.t('table.name', 'Name'),
  dataIndex: 'name',
  sorter: sortBy('name'),
  filterProperty: 'name',
  get: (record: any) => get(record, 'name'),
  render: (name: string) => truncateTooltipper(name, 60),
  edit(record: any, { onChange, ...props }: { onChange: (data: any) => void }) {
    const { name } = record || {};
    return (
      <Input
        style={{ maxWidth: '300px' }}
        defaultValue={name}
        onChange={onChange && (e => onChange({ name: e.target.value }))}
        {...props}
      />
    );
  },
};

const statusFilters = Object.values(TASK_STATUSES).map(value => ({
  text: startCase(value),
  value,
}));

const statusFilterConfig = {
  key: 'status',
  type: 'string',
  filterAttr: '',
  exactMatch: false,
  values: '',
  includeNoneOption: false,
};

export const status = {
  title: i18n.t('table.status', 'Status'),
  dataIndex: 'status',
  key: 'status',
  sorter: sortBy('status'),
  filterProperty: 'status',
  filters: statusFilters,
  filterDropdown: getFilterDropdown(statusFilterConfig),
  onFilter: getOnFilter(statusFilterConfig),
  get: (record: any) => get(record, 'status'),
  render: (status: AtlasGqlTaskStatus) => (status ? <StatusTag status={status} /> : <None />),
  edit(record: any, { onChange, ...props }: { onChange: (data: any) => void }) {
    const { status: value } = record || {};
    return (
      <Select
        defaultValue={value}
        style={{ minWidth: '100px' }}
        onChange={onChange && (status => onChange({ status }))}
        {...props}
      >
        {Object.values(TASK_STATUSES).map(status => (
          <Select.Option key={status} value={status}>
            {startCase(status)}
          </Select.Option>
        ))}
      </Select>
    );
  },
};

const completedDateFilterConfig = {
  key: 'completedDate',
  type: 'date',
  filterAttr: '',
  exactMatch: '',
  values: '',
  includeNoneOption: false,
};

export const completedDate = {
  title: i18n.t('table.completedDate', 'Completed Date'),
  dataIndex: 'completedDate',
  key: 'completedDate',
  sorter: sortBy('completedDate'),
  get: (record: any) => get(record, 'completedDate'),
  render: (completedDate: string) => (completedDate ? formatDate(completedDate) : <None />),
  getInitialValue: (record: any) =>
    record.completedDate ? moment(record.completedDate) : undefined,
  filterDropdown: getFilterDropdown(completedDateFilterConfig),
  onFilter: getOnFilter(completedDateFilterConfig),
  edit(record: any, { onChange, ...props }: { onChange: (data: any) => void }) {
    const { completedDate: value } = record || {};
    return (
      <UTCDatePicker
        defaultValue={value && moment(value)}
        onChange={(date: Moment | null) => {
          onChange?.({ completedDate: date });
        }}
        {...props}
      />
    );
  },
};

const dueDateFilterConfig = {
  key: 'dueDate',
  type: 'date',
  filterAttr: '',
  exactMatch: '',
  values: '',
  includeNoneOption: false,
};

const OverdueTag: React.FC<TagProps> = props => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Tooltip title={t('table.overdue', 'Overdue')}>
      <Tag color={theme.red}>{props.children}</Tag>
    </Tooltip>
  );
};

export const dueDate = {
  title: i18n.t('table.dueDate', 'Due Date'),
  dataIndex: 'dueDate',
  key: 'due_date',
  // @ts-ignore - The inferred type for defaultNone input in JS is not happy even though it only takes in a renderFn
  render: defaultNone((dueDate, record) => {
    const formattedDate = formatDate(dueDate);

    if (!isOverdue(record)) {
      return formattedDate;
    }

    return <OverdueTag>{formattedDate}</OverdueTag>;
  }),
  filterProperty: 'dueDate',
  sorter: sortBy('dueDate'),
  get: (record: any) => get(record, 'dueDate'),
  getInitialValue: (record: any) => (record.dueDate ? moment(record.dueDate) : undefined),
  filterDropdown: getFilterDropdown(dueDateFilterConfig),
  onFilter: getOnFilter(dueDateFilterConfig),
  edit(record: any, { onChange, ...props }: { onChange: (data: any) => void }) {
    const { dueDate: value } = record || {};
    return (
      <UTCDatePicker
        defaultValue={value && moment(value)}
        onChange={(date: Moment | null) => {
          onChange?.({ dueDate: date });
        }}
        {...props}
      />
    );
  },
};

/*
 * This function builds the header column for forOrganization.
 *
 * @param organizations - array of orgs with delivery relationships for the user
 * @param isEditor - boolean based on Horizon role
 * @param features - object based on Horizon feature flags
 * @param task - inspection task the request is being made against
 * @param handleForOrgSave - function to save the forOrganization field
 * @returns an inlineEditable column for the forOrganization field
 */
export const getForOrganization = (
  organizations: Pick<AtlasGqlOrganization, 'id' | 'name'>[],
  isEditor: boolean,
  features: Record<string, boolean>,
  task: AtlasGqlInspectionTask,
  handleForOrgSave?: (input: any) => Promise<void> | undefined
) => {
  const { FOR_ORGANIZATION_EDIT } = features;

  const workingTaskCopy = { ...task };
  const { forOrg } = workingTaskCopy;

  // Check if we have deliveries. If not, set to empty array.
  const deliveries = task?.inspection?.deliveries ?? [];

  // Check if we can edit the for org field
  const canEditForOrg = () => {
    // If it's been delivered already no one can change it
    if (deliveries.length > 0) {
      return false;
    }

    // If there's currently a forOrganization set only those with FOR_ORGANIZATION_EDIT can set it
    if (FOR_ORGANIZATION_EDIT) {
      return true;
    }

    // Editors without FOR_ORGANIZATION_EDIT can set it if there's no forOrganization set yet
    if (!forOrg && isEditor) {
      return true;
    }

    // Otherwise, default to false
    return false;
  };

  const handleEditChange = (organizationId: string) => {
    const updatedOrg = organizations.find(o => o.id === organizationId);
    // In order to properly propagate the edit state back to the Input of the inlineEditable, we need to directly modify the forOrg field on the task record
    // @ts-ignore - The slug field isn't present here, but that's fine
    task.forOrg = updatedOrg;
  };

  const columnDef = {
    key: 'forOrg',
    title: i18n.t('inspection.forOrganization', 'For Organization'),
    dataIndex: 'forOrg',
    get: (record: AtlasGqlInspectionTask) => get(record, 'forOrg'),
    render: (forOrg: AtlasGqlOrganization | null) => {
      const renderTooltip = () => {
        // No one can edit if it has been delivered
        if (deliveries.length > 0) {
          return i18n.t(
            'tasks.forOrgDeliveredTooltip',
            'Field cannot be edited because associated inspection has already been delivered to target organization.'
          );
        }

        // Default to nothing
        return '';
      };

      return <Tooltip title={renderTooltip()}>{forOrg?.name ?? <None />}</Tooltip>;
    },
    ...(canEditForOrg() &&
      handleForOrgSave && {
        edit(record: AtlasGqlInspectionTask, { onChange }: any) {
          // The edit functionality is a modal with safety checks around the For Organization
          // But to continue working with inlineEditable, there's also a disabled Input displayed within this edit to preview the changes made in the modal before commiting them.
          return (
            <>
              <Input
                // Always keep this field disabled
                // We want the updated value displayed, but users shouldn't be able to further modify the value once the modal closes
                disabled={true}
                style={{ maxWidth: '300px' }}
                value={record?.forOrg?.name ?? ''}
              />
              <TaskForOrganizationEditor
                assetId={task.asset.id}
                defaultOrgId={task?.forOrg?.id}
                onSave={e => {
                  handleEditChange(e);
                  onChange({ forOrganization: e });
                }}
              />
            </>
          );
        },
        // Editing access is restricted by feature flag. `TASK_EDIT` is turned on for the editor role.
        editRule: 'TASK_EDIT',
      }),
  };

  return inlineEditable(columnDef, { onSave: handleForOrgSave });
};

export const getMostRecentDeliveryForTargetCustomer = (
  deliveries: AtlasGqlInspectionDelivery[],
  forOrganizationId: string
) => {
  return deliveries
    ?.filter(d => d?.targetOrganizationId === forOrganizationId)
    .sort((a, b) => new Date(a.created || '').getTime() - new Date(b.created || '').getTime())[
    deliveries.length - 1
  ];
};

const deliveryStatuses = [...Object.values(AtlasGqlInspectionDeliveryStatus), 'Not Delivered'];

const deliveryStatusFilterConfig = {
  key: 'deliveryStatus',
  type: 'string',
  values: deliveryStatuses,
  includeNoneOption: true,
};

export const deliveryStatus = {
  key: 'deliveryStatus',
  title: i18n.t('table.deliveryStatus', 'Delivery Status'),
  dataIndex: ['inspection', 'deliveries'],
  get: (record: AtlasGqlInspectionTask) => get(record, ['inspection', 'deliveries']),
  render: (deliveries: AtlasGqlInspectionDelivery[], record: AtlasGqlInspectionTask) => {
    if (record?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask) {
      const delivery = getMostRecentDeliveryForTargetCustomer(deliveries, record?.forOrg?.id ?? '');
      const status = delivery?.status;
      return status ? <StatusTag status={status} /> : i18n.t('table.notDelivered', 'Not Delivered');
    }

    return <None />;
  },
};

export const clientDeliveryStatus = {
  ...deliveryStatus,
  filters: deliveryStatuses.map(s => {
    return { text: startCase(s), value: s };
  }),
  filterDropdown: getFilterDropdown(deliveryStatusFilterConfig),
  onFilter: (filterValue: string, record: AtlasGqlInspectionTask): boolean => {
    const status =
      record?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            record.inspection?.deliveries ?? [],
            record.forOrg?.id ?? ''
          )?.status ?? 'Not Delivered'
        : '';

    return filterValue.trim() === status;
  },
  sorter: (taskA: AtlasGqlInspectionTask, taskB: AtlasGqlInspectionTask) => {
    const statusA =
      taskA.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            taskA.inspection?.deliveries ?? [],
            taskA.forOrg?.id ?? ''
          )?.status ?? 'Not Delivered'
        : '';
    const statusB =
      taskB.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            taskB.inspection?.deliveries ?? [],
            taskB.forOrg?.id ?? ''
          )?.status ?? 'Not Delivered'
        : '';

    return statusA || statusB ? naturalSort(statusA, statusB) : 0;
  },
};

const deliveryDateFilterConfig = {
  key: 'deliveryDate',
  type: 'date',
  includeNoneOption: true,
};

export const deliveryDate = {
  key: 'deliveryDate',
  title: i18n.t('table.dateDelivered', 'Date Delivered'),
  dataIndex: ['inspection', 'deliveries'],
  get: (record: AtlasGqlInspectionTask) => get(record, ['inspection', 'deliveries']),
  render: (deliveries: AtlasGqlInspectionDelivery[], record: AtlasGqlInspectionTask) => {
    if (record?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask) {
      const delivery = getMostRecentDeliveryForTargetCustomer(deliveries, record?.forOrg?.id ?? '');
      const created = delivery?.created;
      return created ? formatDate(created) : i18n.t('common.notApplicable', 'N/A');
    }

    return <None />;
  },
};

export const clientDeliveryDate = {
  ...deliveryDate,
  filterDropdown: getFilterDropdown(deliveryDateFilterConfig),
  onFilter: (filterValue: string | string[], record: AtlasGqlInspectionTask): boolean => {
    const created =
      record?.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            record.inspection?.deliveries ?? [],
            record.forOrg?.id ?? ''
          )?.created ?? 'N/A'
        : '';

    if (isString(filterValue) && filterValue.trim() === '') {
      return created === 'N/A' || created === '';
    }

    const startDate = new Date(filterValue[0]);
    const endDate = new Date(filterValue[1]);
    const createdDate = new Date(created);

    return createdDate >= startDate && createdDate <= endDate;
  },
  sorter: (taskA: AtlasGqlInspectionTask, taskB: AtlasGqlInspectionTask) => {
    const createdA =
      taskA.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            taskA.inspection?.deliveries ?? [],
            taskA.forOrg?.id ?? ''
          )?.created ?? 'N/A'
        : '';
    const createdB =
      taskB.type?.type === AtlasGqlWorkContainerTypeOption.InspectionTask
        ? getMostRecentDeliveryForTargetCustomer(
            taskB.inspection?.deliveries ?? [],
            taskB.forOrg?.id ?? ''
          )?.created ?? 'N/A'
        : '';

    return createdA || createdB ? naturalSort(createdA, createdB) : 0;
  },
};

// Factory function for creating all translated common columns
export const createTranslatedCommonColumns = (t: (key: string) => string) => ({
  created: {
    ...created,
    title: t('table.created', 'Created'),
  },
  createdBy: {
    ...createdBy,
    title: t('table.createdBy', 'Created By'),
  },
  createdAndCreatedBy: {
    ...createdAndCreatedBy,
    title: t('table.created', 'Created'),
  },
  shortDescription: {
    ...shortDescription,
    title: t('table.description', 'Description'),
  },
  description: {
    ...description,
    title: t('table.description', 'Description'),
  },
  name: {
    ...name,
    title: t('table.name', 'Name'),
  },
  status: {
    ...status,
    title: t('table.status', 'Status'),
  },
  dueDate: {
    ...dueDate,
    title: t('table.dueDate', 'Due Date'),
  },
});
